import { Box, Flex, Heading, HStack, Icon, SimpleGrid, Spinner, Text, Tooltip, VStack } from "@chakra-ui/react";
import { observer } from "mobx-react";
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { FiPlay, FiClock, FiEdit3, FiCheckCircle, FiUsers, FiCheckSquare, FiUserCheck, FiUserPlus, FiBarChart2, FiPlus } from "react-icons/fi";
import { withModel, VOTING_STATUSES } from "@lubbezposrednio-frontend/core";
import { OrganizerPage } from "../OrganizerDashboardView/components/OrganizerShell";
import { OrganizerDashboardModel } from "../OrganizerDashboardView/OrganizerDashboardModel";
import { DashboardTabs } from "../OrganizerDashboardView/components/DashboardTabs";
import { DonutChart } from "../../components/charts/DonutChart";

interface Props {
  model: OrganizerDashboardModel;
}

const STATUS_META: Record<string, { label: string; color: string; bg: string; icon: React.ElementType }> = {
  [VOTING_STATUSES.ONGOING]: { label: "Trwające", color: "#1f9e18", bg: "rgba(47,206,37,0.12)", icon: FiPlay },
  [VOTING_STATUSES.PLANNED]: { label: "Planowane", color: "#430098", bg: "rgba(67,0,152,0.10)", icon: FiClock },
  [VOTING_STATUSES.DRAFT]: { label: "Szkice", color: "#6b7280", bg: "rgba(107,114,128,0.12)", icon: FiEdit3 },
  [VOTING_STATUSES.FINISHED]: { label: "Zakończone", color: "#2563eb", bg: "rgba(37,99,235,0.10)", icon: FiCheckCircle }
};

const STAT_ORDER = [VOTING_STATUSES.ONGOING, VOTING_STATUSES.PLANNED, VOTING_STATUSES.DRAFT, VOTING_STATUSES.FINISHED];

const StatCard: React.FC<{ status: VOTING_STATUSES; count: number }> = ({ status, count }) => {
  const m = STATUS_META[status];
  return (
    <Box
      as={Link}
      to="/organizator/twoje-glosowania"
      bg="white"
      borderRadius="18px"
      border="1px solid rgba(67,0,152,0.08)"
      boxShadow="0 6px 20px rgba(67,0,152,0.05)"
      p="22px"
      transition="all 0.18s"
      _hover={{ transform: "translateY(-3px)", boxShadow: "0 14px 30px rgba(67,0,152,0.12)" }}
    >
      <Flex w="44px" h="44px" align="center" justify="center" bg={m.bg} borderRadius="12px" mb="14px">
        <Icon as={m.icon} boxSize="22px" color={m.color} />
      </Flex>
      <Heading fontSize="34px" color="primary" lineHeight="1">
        {count}
      </Heading>
      <Text fontSize="14px" color="gray.500" mt="6px" fontWeight="600">
        {m.label}
      </Text>
    </Box>
  );
};

const SummaryStat: React.FC<{ icon: React.ElementType; value: number; label: string; hint: string }> = ({ icon, value, label, hint }) => (
  <Tooltip label={hint} hasArrow placement="top" borderRadius="8px" px="12px" py="8px" fontSize="13px" bg="primary" maxW="260px" openDelay={150}>
    <HStack spacing="11px" cursor="default">
      <Flex w="40px" h="40px" align="center" justify="center" bg="rgba(67,0,152,0.06)" borderRadius="11px" flexShrink={0}>
        <Icon as={icon} boxSize="19px" color="primary" />
      </Flex>
      <Box>
        <Text fontSize="20px" fontWeight="800" color="primary" lineHeight="1">
          {value}
        </Text>
        <Text fontSize="12.5px" color="gray.500" borderBottom="1px dotted rgba(107,114,128,0.4)" w="fit-content">
          {label}
        </Text>
      </Box>
    </HStack>
  </Tooltip>
);

const StatsView: React.FC<Props> = observer(({ model }) => {
  useEffect(() => {
    document.title = "Statystyki";
  }, []);

  const totalVotings = STAT_ORDER.reduce((sum, s) => sum + model.countBy(s), 0);
  const statusSegments = STAT_ORDER.map(s => ({ count: model.countBy(s), color: STATUS_META[s].color }))
    .filter(x => x.count > 0)
    .map(x => ({ value: totalVotings ? (x.count / totalVotings) * 100 : 0, color: x.color }));

  return (
    <OrganizerPage>
      <VStack align="stretch" spacing="26px">
        <DashboardTabs value="/organizator/statystyki" />

        <Heading fontSize={{ base: "24px", md: "29px" }} color="primary" fontWeight="800">
          Statystyki
        </Heading>

        {model.isLoading ? (
          <Flex justify="center" py="80px">
            <Spinner color="primary" size="lg" thickness="3px" />
          </Flex>
        ) : !model.isOrganizer ? (
          <Flex direction="column" align="center" justify="center" py="60px" bg="white" borderRadius="20px" border="1px dashed rgba(67,0,152,0.2)">
            <Flex w="56px" h="56px" borderRadius="16px" bg="rgba(67,0,152,0.06)" align="center" justify="center" mb="14px">
              <Icon as={FiBarChart2} boxSize="26px" color="primary" />
            </Flex>
            <Text color="primary" fontWeight="700" mb="4px">
              Brak statystyk
            </Text>
            <Text color="gray.500" fontSize="14px" mb="18px" textAlign="center" maxW="360px">
              Statystyki pojawią się, gdy utworzysz pierwsze głosowanie.
            </Text>
            <HStack as={Link} to="/organizator/utworz-glosowanie" spacing="8px" color="white" bg="accent" px="22px" py="12px" borderRadius="12px" fontWeight="700">
              <FiPlus />
              <Text>Utwórz głosowanie</Text>
            </HStack>
          </Flex>
        ) : (
          <VStack align="stretch" spacing="26px">
            <SimpleGrid columns={{ base: 2, lg: 4 }} spacing={{ base: "14px", md: "20px" }}>
              {STAT_ORDER.map(s => (
                <StatCard key={s} status={s} count={model.countBy(s)} />
              ))}
            </SimpleGrid>

            <Flex bg="white" borderRadius="16px" border="1px solid rgba(67,0,152,0.08)" boxShadow="0 6px 20px rgba(67,0,152,0.05)" p={{ base: "20px", md: "24px 30px" }} gap={{ base: "20px", md: "36px" }} wrap="wrap" align="center">
              {totalVotings > 0 && (
                <DonutChart segments={statusSegments} size={104} thickness={12}>
                  <Text fontSize="22px" fontWeight="800" color="primary" lineHeight="1">
                    {totalVotings}
                  </Text>
                  <Text fontSize="10px" color="gray.400">
                    głosowań
                  </Text>
                </DonutChart>
              )}
              <SummaryStat icon={FiUsers} value={model.activeVotersCount} label="uprawnionych w aktywnych" hint="Liczba uprawnionych w Twoich trwających i planowanych głosowaniach. Liczona osobno dla każdego głosowania (ta sama osoba w kilku głosowaniach liczy się wielokrotnie)." />
              <SummaryStat icon={FiCheckSquare} value={model.totalVotes} label="oddanych głosów" hint="Łącznie głosów oddanych we wszystkich Twoich głosowaniach." />
              <SummaryStat icon={FiUserCheck} value={model.participations.length} label="biorę udział" hint="W ilu głosowaniach jesteś uprawnionym głosującym." />
              {model.myGroupsCount > 0 && <SummaryStat icon={FiUsers} value={model.myGroupsCount} label="moich grup" hint="Do ilu grup należysz jako członek." />}
              {model.openSignupsCount > 0 && <SummaryStat icon={FiUserPlus} value={model.openSignupsCount} label="otwartych zapisów" hint="Twoje głosowania z aktualnie otwartymi zapisami (vox populi)." />}
            </Flex>
          </VStack>
        )}
      </VStack>
    </OrganizerPage>
  );
});

const StatsViewWithModel = withModel<OrganizerDashboardModel>(StatsView, OrganizerDashboardModel);

export default StatsViewWithModel;
